import axios from 'axios'

const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN
const CLIENT_ID = process.env.REACT_APP_CLIENT_ID

/**
 * Check if token is expired or about to expire
 * @param {string} token - JWT token
 * @returns {boolean} True if token is expired or expires within 5 minutes
 */
export const isTokenExpired = (token) => {
  if (!token) return true
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    const currentTime = Math.floor(Date.now() / 1000)
    // Consider token expired if it expires within 1 minute
    return payload.exp < (currentTime + 60)
  } catch (error) {
    console.error('Error checking token expiration:', error)
    return true
  }
}

/**
 * Refresh tokens using refresh token
 * @returns {Promise<boolean>} True if refresh successful
 */
export const refreshTokens = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken')
    if (!refreshToken) {
      throw new Error('No refresh token available')
    }

    const response = await axios.post(`${COGNITO_DOMAIN}/oauth2/token`, 
      new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: CLIENT_ID,
        refresh_token: refreshToken
      }).toString(),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    )

    const { access_token, id_token } = response.data
    
    if (access_token) {
      localStorage.setItem('accessToken', access_token)
    }
    if (id_token) {
      localStorage.setItem('idToken', id_token)
    }

    return true
  } catch (error) {
    console.error('Token refresh failed:', error)
    // Clear all tokens on refresh failure
    localStorage.removeItem('accessToken')
    localStorage.removeItem('idToken')
    localStorage.removeItem('refreshToken')
    return false
  }
}

/**
 * Get valid token, refreshing if necessary
 * @returns {Promise<string|null>} Valid token or null if refresh failed
 */
export const getValidToken = async () => {
  // Skip token refresh on login page
  if (window.location.pathname === '/login') {
    return localStorage.getItem('idToken')
  }
  
  const idToken = localStorage.getItem('idToken')
  
  if (!isTokenExpired(idToken)) {
    return idToken
  }

  // Token is expired, try to refresh
  const refreshSuccess = await refreshTokens()
  if (refreshSuccess) {
    return localStorage.getItem('idToken')
  }

  return null
}